<template>
  <section class="relative isolate overflow-hidden bg-white px-6 py-24 sm:py-32 border-t border-gray-100">
    <!-- Background (no boxes/frames) -->
    <div class="absolute inset-0 -z-10">
    <!-- soft vertical wash -->
    <div class="absolute inset-0 bg-gradient-to-b from-surface-50 via-white to-white"></div>

    <!-- top glow -->
    <div
        class="absolute left-1/2 top-[-18rem] h-[34rem] w-[34rem] -translate-x-1/2 rounded-full blur-3xl opacity-40"
        style="background: radial-gradient(circle, rgba(37,99,235,0.25) 0%, rgba(37,99,235,0) 65%);"
    />

    <!-- subtle diagonal sheen -->
    <div class="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary-50/60 to-transparent"></div>

    <!-- very light grid pattern -->
    <svg class="absolute inset-0 h-full w-full stroke-gray-200/60" aria-hidden="true">
        <defs>
        <pattern id="cert-grid" width="64" height="64" patternUnits="userSpaceOnUse">
            <path d="M64 0H0V64" fill="none" />
        </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#cert-grid)" />
    </svg>
    </div>
    <div class="mx-auto max-w-7xl lg:px-8">
      <div class="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
        <div class="lg:order-2">
            <div
                class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-700 ring-1 ring-primary-200"
                >
                <IconShieldCheck class="h-4 w-4" aria-hidden="true" />
                ISO 24089 Certification
                </div>
            <h2 class="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Raising the Standard for Secure Trailer Software Management
            </h2>

            <p class="mt-6 text-lg leading-8 text-gray-700">
                Haldex SUMS has achieved ISO 24089 certification, demonstrating our commitment to delivering a secure, transparent, 
                and resilient software update ecosystem for the trailer industry. This certification reflects our dedication to robust 
                engineering practices, cybersecurity‑by‑design, and uncompromising process integrity across every stage of software lifecycle management.
            </p>

            <p class="mt-6 text-lg leading-8 text-gray-700">
                By meeting the rigorous requirements of ISO 24089, Haldex provides trailer manufacturers with a dependable, 
                future‑ready foundation for managing software updates with confidence. Combined with built‑in support for 
                <b>UN/ECE R156</b>   compliance, the certification reinforces SUMS as a trusted solution that helps manufacturers meet 
                evolving regulatory expectations while ensuring long‑term operational reliability across connected trailer systems.
            </p>
            <div class="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
                <!-- Primary: gradient + subtle glow -->
                <a
                    :href="certUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="group relative inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-sm
                        bg-gradient-to-r from-primary-700 via-primary-600 to-primary-500
                        hover:from-primary-600 hover:via-primary-600 hover:to-primary-400
                        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                    <span
                    class="pointer-events-none absolute inset-0 -z-10 rounded-lg blur-xl opacity-40 transition group-hover:opacity-60"
                    style="background: radial-gradient(circle, rgba(37,99,235,0.55) 0%, rgba(37,99,235,0) 65%);"
                    />
                    <IconFileCertificate class="h-5 w-5" aria-hidden="true" />
                    View certification
                    <IconArrowRight class="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </a>

                <!-- Secondary: “glass” outline -->
                <a
                    :href="infoUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold
                        text-primary-800 ring-1 ring-inset ring-primary-200
                        bg-white/50 backdrop-blur-sm
                        hover:bg-primary-50/70 hover:ring-primary-300
                        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                    Learn more
                    <IconExternalLink class="h-4 w-4" aria-hidden="true" />
                </a>
            </div>
        </div>

        <div class="flex justify-center lg:justify-start lg:order-1">
          <img
            class="w-full max-w-lg rounded-2xl object-contain shadow-[0_30px_60px_-35px_rgba(2,6,23,0.75)]"
            src="/images/home/iso-24089-certification.png"
            alt="ISO 24089 Certification"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { IconShieldCheck, IconArrowRight, IconExternalLink, IconFileCertificate } from '@tabler/icons-vue';

const certUrl = 'docs/certifications/Certificate_Haldex_SUMS_ISO24089.pdf';
const infoUrl = 'https://www.iso.org/standard/77796.html';
</script>